import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import t from '~t';
import config from '~config';
import { useSelector, useDispatch } from 'react-redux';
import {
  clientUpdate,
  clientResetToken,
  clientTestTokenCreate,
  clientTestTokenLoad,
  clientRevoke,
  clientIconUpload,
} from '~data/actions/oauth';
import { makeClient, getTestToken } from '~data/selectors/oauth';

import { Error, Confirm } from '~co/overlay/dialog';
import { Layout, Label, Text, ButtonRow, Title, Separator, Button, Modal, ModalHeader as Header, ModalContent as Content, Icon } from '~ui';

export default function DevEdit({ _id = 0, onClose }) {
  const dispatch = useDispatch();

  const getClient = useMemo(() => makeClient(), []);
  const _client = useSelector((state) => getClient(state, _id));
  const testToken = useSelector((state) => getTestToken(state, _id));

  const [loading, setLoading] = useState(false);
  const [unsaved, setUnsaved] = useState(false);
  const [client, setClient] = useState(_client);
  const iconInput = useRef(null);

  useEffect(() => {
    dispatch(clientTestTokenLoad(_id));
  }, [dispatch, _id]);

  useEffect(() => {
    setClient(_client);
  }, [_client]);

  const onTextChange = useCallback((e) => {
    const key = e.currentTarget.name;
    const val = e.currentTarget.value;

    setUnsaved(true);
    setClient((client) => ({
      ...client,
      ...(Array.isArray(client[key]) ? { [key]: [val] } : { [key]: val }),
    }));
  }, []);

  const onSubmit = useCallback(
    (e) => {
      e.preventDefault();
      setLoading(true);

      dispatch(
        clientUpdate(
          _id,
          client,
          () => {
            setLoading(false);
            setUnsaved(false);
          },
          (e) => {
            setLoading(false);
            Error(e);
          },
        ),
      );
    },
    [dispatch, _id, client],
  );

  const onIconUpload = useCallback(
    (e) => {
      if (!e.target.files[0]) return;
      setLoading(true);

      dispatch(
        clientIconUpload(
          _id,
          e.target.files[0],
          () => setLoading(false),
          (e) => {
            setLoading(false);
            Error(e);
          },
        ),
      );
    },
    [dispatch, _id],
  );

  const onResetSecretClick = useCallback(
    async (e) => {
      e.preventDefault();

      if (await Confirm(t.s('areYouSure')))
        dispatch(clientResetToken(_id, () => {}, Error));
    },
    [dispatch, _id],
  );

  const onCreateTestTokenClick = useCallback(
    async (e) => {
      e.preventDefault();

      if (await Confirm(t.s('areYouSure')))
        dispatch(clientTestTokenCreate(_id, () => {}, Error));
    },
    [dispatch, _id],
  );

  const onRevokeAllTokensClick = useCallback(
    async (e) => {
      e.preventDefault();

      if (await Confirm(t.s('areYouSure')))
        dispatch(clientRevoke(_id, () => {}, Error));
    },
    [dispatch, _id],
  );

  const {
    _id: clientId,
    name,
    description,
    site,
    redirects: [redirect = ''],
    secret,
    icon,
  } = client;

  return (
    <Modal className='w-[11rem]' onClose={onClose}>
      <Header title={t.s('app')} />

      <Content>
        <form onSubmit={onSubmit}>
          <Layout type='grid'>
            <Label>{t.s('name')}</Label>
            <Text
              autoFocus
              required
              disabled={loading}
              value={name}
              name='name'
              onChange={onTextChange}
            />

            <Label>{t.s('description')}</Label>
            <Text
              required
              autoSize
              disabled={loading}
              value={description}
              name='description'
              onChange={onTextChange}
            />

            <Label>{t.s('site')}</Label>
            <Text
              required
              autoSize
              disabled={loading}
              value={site}
              name='site'
              onChange={onTextChange}
            />

            <Label>{t.s('devRedirectUri')}</Label>
            <Text
              required
              autoSize
              disabled={loading}
              value={redirect}
              name='redirects'
              onChange={onTextChange}
            />

            <Label>{t.s('cover')}</Label>
            <div>
              {icon && (
                <>
                  <img src={icon} width='100' />
                  <br />
                </>
              )}

              <Button
                type='button'
                variant='outline'
                disabled={loading}
                onClick={() => iconInput.current?.click()}
              >
                <Icon name='upload' />
                {t.s('upload')}…
              </Button>
              <input
                ref={iconInput}
                type='file'
                accept='image/*'
                className='hidden'
                onChange={onIconUpload}
              />
            </div>

            {unsaved && (
              <ButtonRow>
                <Button type='submit' disabled={loading}>
                  {t.s('save')}
                </Button>
              </ButtonRow>
            )}

            <Separator />

            <Title>{t.s('devCredentials')}</Title>

            <Label>{t.s('devClientId')}</Label>
            <Text readOnly variant='less' value={clientId} />

            <Label>{t.s('devClientSecret')}</Label>
            <div>
              <Text readOnly variant='less' value={secret} />

              <a href='' onClick={onResetSecretClick}>
                {t.s('devResetSecret')}
              </a>
            </div>

            <Label>{t.s('devTestToken')}</Label>
            <div>
              {testToken && (
                <Text readOnly variant='less' value={testToken} />
              )}

              <a href='' onClick={onCreateTestTokenClick}>
                {t.s(
                  testToken ? 'devResetTestToken' : 'devCreateTestToken',
                )}
              </a>

              <br />
              <br />

              <a href={config.links.dev.token} target='_blank'>
                {t.s('howToUse')}
              </a>
            </div>

            <ButtonRow>
              <Button variant='outline' onClick={onRevokeAllTokensClick}>
                {t.s('devRevokeAllTokens')}
              </Button>
            </ButtonRow>
          </Layout>
        </form>
      </Content>
    </Modal>
  );
}
